import { useQueryClient } from '@tanstack/react-query';
import { api, sendForm, unwrap, type Schemas } from '../../api/client';
import { useApiQuery } from '../../api/hooks';
import { BRAND } from '../../lib/brand';
import type { RequestType } from '../../lib/help';
import { useTenant } from '../../auth/session';
import { ensureDevUserFor } from '../../pages/groups/data';

/** Requests and What's new are the person's own: in dev sign-in, act as the business's dev user first. */
function useAsUser() {
  const tenant = useTenant();
  return () => ensureDevUserFor(tenant.id);
}

// Help and support data (APP-DESIGN.md section 10, ADR 0046). Requests are the person's own, but
// cached under the company like every query, so switching companies never shows stale data.

export type SupportRequest = Schemas['SupportRequest'];
export type SupportRequestDetail = Schemas['SupportRequestDetail'];
export type HelpArticleSummary = Schemas['HelpArticleSummary'];
export type HelpArticle = Schemas['HelpArticle'];
export type SupportAccess = Schemas['SupportAccess'];
export type WhatsNew = Schemas['WhatsNew'];

/** The Help Center locale: South African English for a ZA browser, else US English. */
export const helpLocale = (): 'en-US' | 'en-ZA' =>
  typeof navigator !== 'undefined' && /-ZA$/i.test(navigator.language) ? 'en-ZA' : 'en-US';

export const TYPE_TITLE: Record<RequestType, string> = {
  bug: "Something isn't working",
  howto: 'How do I…?',
  billing: 'Billing and my plan',
  feature: 'Suggest a feature',
};
export const TYPE_SUB: Record<RequestType, string> = {
  bug: 'Report a problem with a screen or a number',
  howto: `Ask how to do something in ${BRAND.name}`,
  billing: 'Invoices, payment method, changing plan',
  feature: `Tell us what ${BRAND.name} should do next`,
};
/** The two fields of each form (10.3). */
export const TYPE_FIELDS: Record<RequestType, [string, string]> = {
  bug: ['What went wrong?', 'Tell us more'],
  howto: ['What are you trying to do?', 'Tell us more'],
  billing: ['What is it about?', 'Tell us more'],
  feature: [`What would you like ${BRAND.name} to do?`, 'How would it help you?'],
};
export const TYPE_PLACEHOLDER: Record<RequestType, string> = {
  bug: 'What you did, what you expected, and what happened',
  howto: '',
  billing: '',
  feature: '',
};

/** A request's status as a dot and a word (10.4): the class picks the dot's colour. */
export function statusLabel(r: Pick<SupportRequest, 'type' | 'status' | 'feature_status'>): { cls: string; label: string } {
  if (r.type === 'feature') {
    switch (r.feature_status) {
      case 'planned': return { cls: 'open', label: 'Planned' };
      case 'building': return { cls: 'open', label: 'Building' };
      case 'released': return { cls: 'paid', label: 'Released' };
      case 'not_planned': return { cls: 'draft', label: 'Not planned' };
      default: return { cls: 'draft', label: 'Received' };
    }
  }
  if (r.status === 'waiting') return { cls: 'pending', label: 'Waiting on you' };
  if (r.status === 'solved') return { cls: 'paid', label: 'Solved' };
  return { cls: 'open', label: 'Open' };
}

export function useSupportStatus() {
  return useApiQuery(['support', 'status'], async () => (await unwrap(api.GET('/v1/support/status'))).data as Schemas['SupportStatus'], { staleTime: 5 * 60_000 });
}

export function useSupportRequests(view: 'all' | 'open' | 'solved' | 'feature' = 'all', limit = 50, enabled = true) {
  const asUser = useAsUser();
  return useApiQuery(['support', 'requests', view, limit], async () => {
    await asUser();
    const r = await unwrap(api.GET('/v1/support/requests', { params: { query: { view, limit } } }));
    return { requests: (r.data ?? []) as SupportRequest[], updates: r.meta?.updates ?? 0 };
  }, { staleTime: 30_000, enabled, refetchInterval: 5 * 60_000 });
}

export function useSupportRequest(id: string) {
  const asUser = useAsUser();
  return useApiQuery(['support', 'request', id], async () => (await asUser(), await unwrap(api.GET('/v1/support/requests/{id}', { params: { path: { id } } }))).data as SupportRequestDetail);
}

export function useArticles(query: string, page: string, enabled = true) {
  return useApiQuery(['support', 'articles', query, query ? '' : page], async () =>
    (await unwrap(api.GET('/v1/support/articles', { params: { query: { ...(query ? { query } : { page }), locale: helpLocale(), brand: BRAND.id } } }))).data as Schemas['HelpArticles'],
  { staleTime: 5 * 60_000, enabled, retry: false });
}

export function useArticle(id: string) {
  return useApiQuery(['support', 'article', id], async () =>
    (await unwrap(api.GET('/v1/support/articles/{id}', { params: { path: { id }, query: { locale: helpLocale(), brand: BRAND.id } } }))).data as HelpArticle,
  { staleTime: 5 * 60_000 });
}

export function useWhatsNew() {
  const asUser = useAsUser();
  return useApiQuery(['support', 'whats-new'], async () => (await asUser(), await unwrap(api.GET('/v1/support/whats-new'))).data as WhatsNew, { staleTime: 10 * 60_000 });
}

export function useMarkWhatsNewSeen() {
  const queryClient = useQueryClient();
  return async () => {
    await unwrap(api.POST('/v1/support/whats-new/seen'));
    queryClient.setQueriesData<WhatsNew>({ predicate: (q) => q.queryKey[1] === 'support' && q.queryKey[2] === 'whats-new' }, (d) => (d ? { ...d, unseen: false } : d));
  };
}

export function useSupportAccess(enabled = true) {
  return useApiQuery(['support', 'access'], async () => (await unwrap(api.GET('/v1/support/access'))).data as SupportAccess, { enabled });
}

export interface SendRequest {
  type: RequestType;
  subject: string;
  body: string;
  page: string;
  browser: string;
  appVersion: string;
  errorId?: string | undefined;
  booksAccess: boolean;
  screenshot?: File | null | undefined;
  /** Talk to a person from the assistant: the conversation goes along as an internal note. */
  assistantConversationId?: string | undefined;
}

export async function sendRequest(r: SendRequest) {
  const form = new FormData();
  form.set('type', r.type);
  form.set('subject', r.subject);
  form.set('body', r.body);
  form.set('page', r.page);
  form.set('browser', r.browser);
  form.set('app_version', r.appVersion);
  if (r.errorId) form.set('error_id', r.errorId);
  form.set('brand', BRAND.id);
  form.set('books_access', r.booksAccess ? 'true' : 'false');
  if (r.screenshot) form.set('screenshot', r.screenshot);
  if (r.assistantConversationId) form.set('assistant_conversation_id', r.assistantConversationId);
  return sendForm<{ request: SupportRequest; books_access_until: string | null }>('/v1/support/requests', form);
}

export async function replyTo(id: string, body: string) {
  return (await unwrap(api.POST('/v1/support/requests/{id}/replies', { params: { path: { id } }, body: { body } }))).data as SupportRequest;
}

export async function removeSupportAccess(id: string) {
  await unwrap(api.POST('/v1/support/access/{id}/remove', { params: { path: { id } } }));
}

/** The page name for the context line: the path's first part, in words. */
export function pageName(path: string): string {
  const first = path.split('?')[0]!.split('/').filter(Boolean)[0];
  if (!first) return 'Dashboard';
  const words = first.replace(/-/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A screenshot the server accepts: a PNG or JPEG of at most 10 MB. Returns the problem, or null. */
export function screenshotProblem(file: { type: string; size: number }): string | null {
  if (file.type !== 'image/png' && file.type !== 'image/jpeg') return 'Choose a PNG or JPEG image.';
  if (file.size > 10 * 1024 * 1024) return 'The image can be at most 10 MB.';
  return null;
}
